'use client'
import * as React from 'react'
import Link from 'next/link'
import { ArrowRight, Check, AlertTriangle, ShieldCheck, ShieldAlert, Info, Search, Loader2, RefreshCw } from 'lucide-react'

// Creator authenticity + engagement scorer. The brand enters the public
// numbers from a creator's profile or media kit, answers three judgement
// checks, and gets a live authenticity score, an engagement-rate read
// against a tier benchmark, a screening estimate of how much of the
// audience looks inauthentic, and a flag list. Pure client state, no
// signup, nothing leaves the browser. It is a screening pass, not a
// forensic audit, and the UI says so.

type Platform = 'instagram' | 'tiktok' | 'youtube'

// Representative engagement-rate floors by follower tier. Engagement
// dilutes as audiences grow, so a healthy macro account looks "worse"
// than a healthy nano account. These are composite benchmark ranges
// drawn from published influencer-marketing reports, not an Idukki
// measurement. `good` = a healthy organic rate for the tier; `floor`
// = the line below which engagement looks thin for the size.
type Tier = { key: string; label: string; min: number; good: number; floor: number }

const TIERS: Record<Platform, Tier[]> = {
  instagram: [
    { key: 'nano',  label: 'Nano (1k–10k)',     min: 0,       good: 4.0, floor: 1.5 },
    { key: 'micro', label: 'Micro (10k–50k)',   min: 10000,   good: 2.5, floor: 1.0 },
    { key: 'mid',   label: 'Mid (50k–500k)',    min: 50000,   good: 1.6, floor: 0.7 },
    { key: 'macro', label: 'Macro (500k–1M)',   min: 500000,  good: 1.2, floor: 0.5 },
    { key: 'mega',  label: 'Mega (1M+)',        min: 1000000, good: 0.9, floor: 0.3 },
  ],
  tiktok: [
    { key: 'nano',  label: 'Nano (1k–10k)',     min: 0,       good: 9.0, floor: 3.0 },
    { key: 'micro', label: 'Micro (10k–50k)',   min: 10000,   good: 7.0, floor: 2.5 },
    { key: 'mid',   label: 'Mid (50k–500k)',    min: 50000,   good: 5.0, floor: 2.0 },
    { key: 'macro', label: 'Macro (500k–1M)',   min: 500000,  good: 3.5, floor: 1.2 },
    { key: 'mega',  label: 'Mega (1M+)',        min: 1000000, good: 2.5, floor: 0.8 },
  ],
  youtube: [
    { key: 'nano',  label: 'Nano (1k–10k)',     min: 0,       good: 3.0, floor: 1.0 },
    { key: 'micro', label: 'Micro (10k–50k)',   min: 10000,   good: 2.2, floor: 0.8 },
    { key: 'mid',   label: 'Mid (50k–500k)',    min: 50000,   good: 1.6, floor: 0.6 },
    { key: 'macro', label: 'Macro (500k–1M)',   min: 500000,  good: 1.1, floor: 0.4 },
    { key: 'mega',  label: 'Mega (1M+)',        min: 1000000, good: 0.8, floor: 0.25 },
  ],
}

const PLATFORM_LABEL: Record<Platform, string> = {
  instagram: 'Instagram',
  tiktok: 'TikTok',
  youtube: 'YouTube',
}

function tierFor(platform: Platform, followers: number): Tier {
  const tiers = TIERS[platform]
  let chosen = tiers[0]
  for (const t of tiers) if (followers >= t.min) chosen = t
  return chosen
}

type Flag = { tone: 'red' | 'amber' | 'green'; text: string }

function clamp(n: number, lo: number, hi: number) {
  return Math.max(lo, Math.min(hi, n))
}

export function CreatorVettingScorer() {
  const [platform, setPlatform] = React.useState<Platform>('instagram')
  const [followers, setFollowers] = React.useState<number | ''>(45000)
  const [likes, setLikes] = React.useState<number | ''>(820)
  const [comments, setComments] = React.useState<number | ''>(14)
  const [following, setFollowing] = React.useState<number | ''>(680)

  // Three judgement checks the numbers can't capture on their own.
  const [smoothGrowth, setSmoothGrowth] = React.useState(true)   // no sudden spikes
  const [genuineComments, setGenuineComments] = React.useState(true) // specific, on-topic
  const [audienceFit, setAudienceFit] = React.useState(true)     // location/demo match

  // Optional live lookup: pull the public numbers by handle so the user
  // doesn't have to copy them. Fills followers/following/likes/comments.
  // Growth pattern and audience fit can't be read from a public profile,
  // so those stay as the manual judgement checks below.
  const [handle, setHandle] = React.useState('')
  const [lookup, setLookup] = React.useState<{
    state: 'idle' | 'loading' | 'error' | 'ok'
    msg?: string
    meta?: { cached: boolean; displayName?: string; postsSampled: number; handle: string }
  }>({ state: 'idle' })

  const runLookup = React.useCallback(async (refresh = false) => {
    const h = handle.trim().replace(/^@/, '')
    if (!h) return
    setLookup({ state: 'loading' })
    try {
      const res = await fetch(
        `/api/tools/creator-authenticity-check?platform=${platform}&handle=${encodeURIComponent(h)}${refresh ? '&refresh=1' : ''}`,
      )
      const json = await res.json().catch(() => ({}))
      if (!res.ok) {
        setLookup({
          state: 'error',
          msg: res.status === 501
            ? 'Live lookup isn’t switched on yet. Enter the numbers manually below.'
            : (json.error ? `${json.error}. Enter the numbers manually below.` : 'Lookup failed. Enter the numbers manually below.'),
        })
        return
      }
      const p = json.profile || {}
      setFollowers(Number(p.followers) || 0)
      setFollowing(Number(p.following) || 0)
      setLikes(Number(p.medianLikes) || 0)
      setComments(Number(p.medianComments) || 0)
      setLookup({
        state: 'ok',
        meta: { cached: !!json.cached, displayName: p.displayName, postsSampled: Number(p.postsSampled) || 0, handle: h },
      })
    } catch {
      setLookup({ state: 'error', msg: 'Lookup failed. Enter the numbers manually below.' })
    }
  }, [handle, platform])

  const f = typeof followers === 'number' ? followers : 0
  const l = typeof likes === 'number' ? likes : 0
  const c = typeof comments === 'number' ? comments : 0
  const fg = typeof following === 'number' ? following : 0

  const result = React.useMemo(() => {
    const tier = tierFor(platform, f)
    const hasNumbers = f > 0 && (l > 0 || c > 0)

    const er = hasNumbers ? ((l + c) / f) * 100 : 0
    const commentRatio = l + c > 0 ? c / (l + c) : 0

    const flags: Flag[] = []

    // 1. Engagement-rate health (0-35).
    let erScore = 0
    if (!hasNumbers) {
      erScore = 0
    } else if (er >= tier.good * 3) {
      // Implausibly high for the tier — classic sign of an engagement pod
      // or bought likes inflating a small post sample.
      erScore = 14
      flags.push({ tone: 'amber', text: `Engagement rate of ${er.toFixed(1)}% is unusually high for ${tier.label.toLowerCase()} — verify it isn't a pod or a cherry-picked post.` })
    } else if (er >= tier.good) {
      erScore = 35
      flags.push({ tone: 'green', text: `Engagement rate of ${er.toFixed(1)}% is healthy for ${tier.label.toLowerCase()}.` })
    } else if (er >= tier.floor) {
      erScore = Math.round(14 + (er - tier.floor) / (tier.good - tier.floor) * 21)
      flags.push({ tone: 'amber', text: `Engagement rate of ${er.toFixed(1)}% is below the ${tier.good}% you'd want for this tier, but not alarming.` })
    } else {
      erScore = Math.round(clamp((er / tier.floor) * 14, 0, 13))
      flags.push({ tone: 'red', text: `Engagement rate of ${er.toFixed(1)}% is under the ${tier.floor}% floor for ${tier.label.toLowerCase()} — a common sign of inflated follower counts.` })
    }

    // 2. Comment quality (0-25): ratio + the genuine-comments check.
    let commentScore = 0
    if (hasNumbers) {
      if (commentRatio < 0.003) {
        commentScore = 6
        flags.push({ tone: 'red', text: `Comments are only ${(commentRatio * 100).toFixed(2)}% of engagement — like-only activity at this level often points to like-bots.` })
      } else if (commentRatio > 0.12) {
        commentScore = 14
        flags.push({ tone: 'amber', text: `Comments are ${(commentRatio * 100).toFixed(1)}% of engagement — unusually high, which can indicate a comment pod.` })
      } else {
        commentScore = 18
      }
    }
    if (genuineComments) {
      commentScore += 7
    } else {
      flags.push({ tone: 'red', text: 'Comments read as generic ("🔥", "nice!", "love it") — a strong tell for bought or pod engagement.' })
    }
    commentScore = clamp(commentScore, 0, 25)

    // 3. Growth pattern (0-20).
    let growthScore = 20
    if (!smoothGrowth) {
      growthScore = 4
      flags.push({ tone: 'red', text: 'Follower count jumped suddenly — organic growth is gradual; spikes usually mean purchased followers.' })
    }

    // 4. Audience fit (0-20).
    let fitScore = 20
    if (!audienceFit) {
      fitScore = 5
      flags.push({ tone: 'amber', text: "Audience location or demographics don't match your market — even real followers in the wrong country won't convert." })
    }

    // Following/follower ratio is a weak side-signal: surface it as a note
    // without heavily weighting the score.
    if (f > 0 && fg > 0 && fg > f && f < 50000) {
      flags.push({ tone: 'amber', text: `Following ${fg.toLocaleString()} while followed by ${f.toLocaleString()} can indicate follow-for-follow growth.` })
    }

    const score = clamp(Math.round(erScore + commentScore + growthScore + fitScore), 0, 100)

    // Screening estimate of inauthentic share. Anchored on a ~15-20%
    // baseline for a typical account, adjusted up by the deficit signals.
    // Bounded and explicitly a heuristic, not a forensic count.
    let fakeEst = 12
    if (hasNumbers && er < tier.floor) fakeEst += 22
    else if (hasNumbers && er < tier.good) fakeEst += 8
    if (hasNumbers && commentRatio < 0.003) fakeEst += 12
    if (!smoothGrowth) fakeEst += 18
    if (!genuineComments) fakeEst += 10
    fakeEst = clamp(Math.round(fakeEst), 5, 80)

    return { tier, er, commentRatio, score, flags, fakeEst, hasNumbers }
  }, [platform, f, l, c, fg, smoothGrowth, genuineComments, audienceFit])

  const verdict = React.useMemo(() => {
    const s = result.score
    if (s >= 80) return { label: 'Looks authentic', tone: 'emerald', icon: ShieldCheck,
      note: 'The signals line up. Still ask for first-party analytics before a paid brief, but this profile clears a screening pass.' }
    if (s >= 60) return { label: 'Mostly healthy, verify the gaps', tone: 'sky', icon: ShieldCheck,
      note: 'A reasonable profile with a couple of things to check. Request the creator’s own audience breakdown and recent reach.' }
    if (s >= 40) return { label: 'Proceed with caution', tone: 'amber', icon: ShieldAlert,
      note: 'Enough warning signs to slow down. Get first-party analytics and a paid test before committing budget.' }
    return { label: 'High risk — do not pay yet', tone: 'rose', icon: ShieldAlert,
      note: 'The numbers don’t support the follower count. Treat the audience as partly inauthentic until proven otherwise.' }
  }, [result.score])

  const TONE: Record<string, { bg: string; text: string; bar: string }> = {
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-700', bar: 'bg-emerald-500' },
    sky: { bg: 'bg-sky-500/10', text: 'text-sky-700', bar: 'bg-sky-500' },
    amber: { bg: 'bg-amber-500/10', text: 'text-amber-700', bar: 'bg-amber-600' },
    rose: { bg: 'bg-rose-500/10', text: 'text-rose-700', bar: 'bg-rose-500' },
  }
  const tone = TONE[verdict.tone]
  const VIcon = verdict.icon

  const numField = (
    label: string,
    value: number | '',
    set: (v: number | '') => void,
    hint?: string,
  ) => (
    <label className="block">
      <span className="text-sm font-medium">{label}</span>
      {hint && <span className="ml-1 text-xs text-muted-foreground">{hint}</span>}
      <input
        type="number"
        inputMode="numeric"
        min={0}
        value={value}
        onChange={(e) => {
          const v = e.target.value
          set(v === '' ? '' : Math.max(0, Number(v)))
        }}
        className="mt-1.5 w-full rounded-xl border bg-card px-3.5 py-2.5 text-sm tabular-nums outline-none transition focus:border-primary/50 focus:ring-2 focus:ring-primary/15"
      />
    </label>
  )

  const checkRow = (
    on: boolean,
    set: (v: boolean) => void,
    label: string,
    sub: string,
  ) => (
    <button
      type="button"
      onClick={() => set(!on)}
      aria-pressed={on}
      className={`flex w-full items-start gap-3 rounded-xl border p-3.5 text-left text-sm transition ${
        on ? 'border-primary/40 bg-primary/5' : 'bg-card hover:border-primary/30'
      }`}
    >
      <span
        className={`mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-md border transition ${
          on ? 'border-primary bg-primary text-primary-foreground' : 'border-muted-foreground/40'
        }`}
      >
        {on && <Check className="h-3.5 w-3.5" />}
      </span>
      <span>
        <span className="font-medium text-foreground">{label}</span>
        <span className="mt-0.5 block text-xs text-muted-foreground">{sub}</span>
      </span>
    </button>
  )

  return (
    <div className="grid gap-8 lg:grid-cols-[1fr_360px] lg:items-start">
      <div className="order-2 space-y-8 lg:order-1">
        <section>
          <h2 className="font-display text-xl font-semibold tracking-tight">The numbers</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Pull these from the creator’s public profile or media kit. Use the median of their last 6–12 posts, not their single best one.
          </p>

          <div className="mt-4">
            <span className="text-sm font-medium">Platform</span>
            <div className="mt-1.5 inline-flex rounded-xl border bg-card p-1">
              {(Object.keys(PLATFORM_LABEL) as Platform[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setPlatform(p)}
                  className={`rounded-lg px-3.5 py-1.5 text-sm font-medium transition ${
                    platform === p ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'
                  }`}
                >
                  {PLATFORM_LABEL[p]}
                </button>
              ))}
            </div>
          </div>

          <div className="mt-4">
            <span className="text-sm font-medium">Look up by handle</span>
            <span className="ml-1 text-xs text-muted-foreground">optional, fills the numbers for you</span>
            <div className="mt-1.5 flex gap-2">
              <div className="relative flex-1">
                <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">@</span>
                <input
                  value={handle}
                  onChange={(e) => setHandle(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); runLookup() } }}
                  placeholder={platform === 'youtube' ? 'channel handle' : 'username'}
                  className="w-full rounded-xl border bg-card py-2.5 pl-7 pr-3 text-sm outline-none transition focus:border-primary/50 focus:ring-2 focus:ring-primary/15"
                />
              </div>
              <button
                type="button"
                onClick={() => runLookup()}
                disabled={lookup.state === 'loading' || !handle.trim()}
                className="inline-flex shrink-0 items-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90 disabled:opacity-50"
              >
                {lookup.state === 'loading' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                Look up
              </button>
            </div>
            {lookup.state === 'error' && (
              <p className="mt-2 text-xs text-amber-600">{lookup.msg}</p>
            )}
            {lookup.state === 'ok' && lookup.meta && (
              <p className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <Check className="h-3.5 w-3.5 text-emerald-600" />
                <span>
                  Pulled @{lookup.meta.handle}
                  {lookup.meta.displayName ? ` (${lookup.meta.displayName})` : ''}
                  {lookup.meta.postsSampled > 0 ? ` from ${lookup.meta.postsSampled} recent posts.` : '.'}
                  {lookup.meta.cached ? ' Cached.' : ' Live.'} Growth and audience fit are still yours to judge below.
                </span>
                <button
                  type="button"
                  onClick={() => runLookup(true)}
                  className="inline-flex items-center gap-1 text-primary underline underline-offset-2"
                >
                  <RefreshCw className="h-3 w-3" /> refresh
                </button>
              </p>
            )}
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            {numField('Followers', followers, setFollowers)}
            {numField('Following', following, setFollowing, '(optional)')}
            {numField(platform === 'youtube' ? 'Median likes / video' : 'Median likes / post', likes, setLikes)}
            {numField(platform === 'youtube' ? 'Median comments / video' : 'Median comments / post', comments, setComments)}
          </div>
        </section>

        <section>
          <h2 className="font-display text-xl font-semibold tracking-tight">The judgement checks</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Three things a follower count can’t tell you. Tick the ones that are true.
          </p>
          <div className="mt-4 space-y-2">
            {checkRow(smoothGrowth, setSmoothGrowth,
              'Growth looks organic',
              'No sudden spikes of tens of thousands of followers in a few days. Check a tool like Social Blade.')}
            {checkRow(genuineComments, setGenuineComments,
              'Comments are specific and on-topic',
              'Real questions and reactions, not a wall of generic emoji or one-word praise.')}
            {checkRow(audienceFit, setAudienceFit,
              'Audience matches your market',
              'The creator’s follower geography and age skew line up with where and to whom you sell.')}
          </div>
        </section>

        <div className="rounded-xl border bg-secondary/40 p-4">
          <div className="flex items-start gap-2.5">
            <Info className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
            <p className="text-xs leading-relaxed text-muted-foreground">
              This is a screening pass, not a forensic audit. It works from the public numbers you enter plus your own judgement.
              Before a paid brief, always ask the creator for a screenshot of their first-party analytics: audience country split,
              age and gender, and reach on recent posts. The benchmark floors here are representative ranges from published
              influencer-marketing reports, not a measurement of this specific account.
            </p>
          </div>
        </div>
      </div>

      <aside className="order-1 lg:order-2 lg:sticky lg:top-24">
        <div className="rounded-2xl border bg-card p-6">
          <p className="text-[11px] font-medium uppercase tracking-[0.16em] text-muted-foreground">Authenticity score</p>
          <div className="mt-2 flex items-baseline gap-2">
            <span className="font-display text-5xl font-semibold tracking-tight tabular-nums">{result.score}</span>
            <span className="text-lg text-muted-foreground">/ 100</span>
          </div>
          <div className="mt-3 h-2 overflow-hidden rounded-full bg-secondary">
            <div className={`h-full rounded-full transition-[width] duration-300 ${tone.bar}`} style={{ width: `${result.score}%` }} />
          </div>

          <div className={`mt-4 flex items-start gap-2.5 rounded-xl ${tone.bg} p-4`}>
            <VIcon className={`mt-0.5 h-5 w-5 shrink-0 ${tone.text}`} />
            <div>
              <p className={`font-display text-base font-semibold ${tone.text}`}>{verdict.label}</p>
              <p className="mt-1 text-sm text-foreground/80">{verdict.note}</p>
            </div>
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-3">
            <div className="rounded-xl border bg-background p-3">
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Engagement rate</dt>
              <dd className="mt-1 font-display text-xl font-semibold tabular-nums">
                {result.hasNumbers ? `${result.er.toFixed(1)}%` : '—'}
              </dd>
              <dd className="text-[11px] text-muted-foreground">healthy ≥ {result.tier.good}%</dd>
            </div>
            <div className="rounded-xl border bg-background p-3">
              <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">Est. inauthentic</dt>
              <dd className="mt-1 font-display text-xl font-semibold tabular-nums">≈{result.fakeEst}%</dd>
              <dd className="text-[11px] text-muted-foreground">screening estimate</dd>
            </div>
          </dl>

          {result.flags.length > 0 && (
            <ul className="mt-4 space-y-2">
              {result.flags.map((fl, i) => {
                const fi = fl.tone === 'green'
                  ? { Icon: Check, c: 'text-emerald-600' }
                  : fl.tone === 'amber'
                    ? { Icon: AlertTriangle, c: 'text-amber-600' }
                    : { Icon: AlertTriangle, c: 'text-rose-600' }
                const FIcon = fi.Icon
                return (
                  <li key={i} className="flex items-start gap-2 text-xs leading-relaxed text-foreground/80">
                    <FIcon className={`mt-0.5 h-3.5 w-3.5 shrink-0 ${fi.c}`} />
                    <span>{fl.text}</span>
                  </li>
                )
              })}
            </ul>
          )}

          <Link
            href="/find-creators"
            className="mt-5 inline-flex w-full items-center justify-center gap-1.5 rounded-xl bg-primary px-4 py-2.5 text-sm font-semibold text-primary-foreground transition hover:opacity-90"
          >
            Skip the guesswork: vetted creators <ArrowRight className="h-4 w-4" />
          </Link>
          <p className="mt-2 text-center text-[11px] text-muted-foreground">
            No signup. This runs entirely in your browser.
          </p>
        </div>
      </aside>
    </div>
  )
}
